/**
 * Cost Register guided intake (from the agent prototype's RegisterIntake). Step 1: pick
 * the cost components the program expects. Step 2: optional planning figures, each saved
 * as an "estimated" line with its basis (the platform never invents amounts). Step 3:
 * anything else about the program's costs.
 */
import { type ReactNode, useState } from 'react'
import {
  Box, Calculator, Cpu, FileText, Flag, Globe, Link2, Search, Shield, Truck, Wrench, X,
} from 'lucide-react'
import Button from '../../components/ui/Button'
import Modal from '../../components/ui/Modal'
import { type IntakeComponent, costRegisterApi } from '../../services/costRegister'

const ICONS: Record<string, ReactNode> = {
  program_management: <Flag />,
  support_equipment: <Box />,
  travel: <Globe />,
  test_evaluation: <Search />,
  integration: <Link2 />,
  training: <FileText />,
  cyber_accreditation: <Shield />,
  data_licenses: <Cpu />,
  sustainment: <Wrench />,
  facilities_ranges: <Calculator />,
  transportation_fielding: <Truck />,
}
const FIELD = 'w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm'
const STEPS = 3

export default function RegisterIntake({ programId, components, onDone, onClose }: {
  programId: string
  components: IntakeComponent[]
  onDone: (createdLines: number) => void
  /** Dismiss without saving; "Guided questions" reopens it. */
  onClose: () => void
}) {
  const [step, setStep] = useState(0)
  const [picked, setPicked] = useState<string[]>([])
  const [amounts, setAmounts] = useState<Record<string, string>>({})
  const [bases, setBases] = useState<Record<string, string>>({})
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const selected = components.filter((c) => picked.includes(c.id))

  const save = async (skipped: boolean) => {
    setSaving(true)
    setError(null)
    try {
      const r = await costRegisterApi.saveIntake(programId, {
        selections: skipped ? [] : selected.map((c) => ({
          id: c.id,
          amount: amounts[c.id]?.replace(/[$,\s]/g, '') || null,
          note: bases[c.id] ?? '',
        })),
        notes: skipped ? '' : notes,
        skipped,
      })
      onDone(r.created_lines)
    } catch (e) {
      setError((e as Error).message)
      setSaving(false)
    }
  }

  return (
    <Modal isOpen onClose={onClose} size="md" aria-label="Setting up your Cost Register">
      <div className="flex flex-col max-h-[80vh]">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Setting up your Cost Register
          </span>
          <span className="ml-auto flex gap-1.5" aria-label={`Step ${step + 1} of ${STEPS}`}>
            {Array.from({ length: STEPS }, (_, i) => (
              <span key={i} className={`w-2 h-2 rounded-full ${i === step ? 'bg-primary' : i < step ? 'bg-muted-foreground' : 'bg-muted'}`} />
            ))}
          </span>
          <Button size="sm" variant="outline" disabled={saving} onClick={() => save(true)}
            title="Record that you skipped; the questions won't open on their own again">
            Skip questions
          </Button>
          <Button size="sm" variant="outline" onClick={onClose} aria-label="Close">
            <X />
          </Button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 space-y-4">
          {step === 0 && (
            <>
              <p className="text-sm text-muted-foreground">
                Beyond the solution itself, program offices pay to manage, equip, travel, test, and field what
                they buy. Which of these do you expect for this program? You can add rough figures next, or
                leave them for later.
              </p>
              <h3 className="text-lg font-semibold">Which cost components do you expect?</h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {components.map((c) => {
                  const on = picked.includes(c.id)
                  return (
                    <button key={c.id} type="button" aria-pressed={on}
                      onClick={() => setPicked(on ? picked.filter((x) => x !== c.id) : [...picked, c.id])}
                      className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                        on ? 'border-primary bg-primary/10' : 'border-border hover:bg-accent/40'}`}>
                      <span className="shrink-0 grid place-items-center w-9 h-9 rounded-md bg-muted text-primary [&_svg]:w-4 [&_svg]:h-4">
                        {ICONS[c.id] ?? <FileText />}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium">{c.label}</span>
                        <span className="block text-xs text-muted-foreground">{c.hint}</span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <h3 className="text-lg font-semibold">Rough planning figures (optional)</h3>
              <p className="text-sm text-muted-foreground">
                Anything you enter becomes a clearly labeled planning line in the register ("estimated", with
                your note as its basis). It's approved for the IGCE since it's your own figure, and editable
                anytime. Leave a figure blank to track the component as expected but unpriced.
              </p>
              {selected.length === 0 && (
                <div className="rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">
                  No components selected. Go back, or finish without figures.
                </div>
              )}
              {selected.map((c) => (
                <div key={c.id} className="grid gap-2 sm:grid-cols-[1fr_9rem_1.5fr] items-end">
                  <div className="text-sm font-medium pb-2">{c.label}</div>
                  <label className="block">
                    <span className="text-xs text-muted-foreground">Amount ($)</span>
                    <input className={FIELD} inputMode="decimal" placeholder="—" value={amounts[c.id] ?? ''}
                      onChange={(e) => setAmounts({ ...amounts, [c.id]: e.target.value })} />
                  </label>
                  <label className="block">
                    <span className="text-xs text-muted-foreground">Basis (what's it based on?)</span>
                    <input className={FIELD} placeholder="e.g. similar prior effort, SWAG, quote…" value={bases[c.id] ?? ''}
                      onChange={(e) => setBases({ ...bases, [c.id]: e.target.value })} />
                  </label>
                </div>
              ))}
            </>
          )}

          {step === 2 && (
            <>
              <h3 className="text-lg font-semibold">Anything else about this program's costs?</h3>
              <textarea className={FIELD} rows={4} value={notes} aria-label="Notes about this program's costs"
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Funding constraints, colors of money, quantities in mind, cost drivers to watch…" />
            </>
          )}
        </div>

        <div className="flex gap-2 mt-4">
          {step > 0 && <Button variant="outline" onClick={() => setStep(step - 1)}>Back</Button>}
          <span className="ml-auto" />
          {step < STEPS - 1 ? (
            <Button onClick={() => setStep(step + 1)}>
              {step === 0 && picked.length === 0 ? 'Continue without selections' : 'Continue'}
            </Button>
          ) : (
            <Button disabled={saving} onClick={() => save(false)}>
              {saving ? 'Saving…' : 'Save & open the register'}
            </Button>
          )}
        </div>
        {error && <div role="alert" className="mt-2 text-sm text-destructive">{error}</div>}
      </div>
    </Modal>
  )
}
